import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, getRecentUsers, removeRecentUser } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/format";
import { toast } from "sonner";
import { Coffee, Eye, EyeOff, Loader2, X } from "lucide-react";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
);

const RecentUsers = ({ users, selected, onPick, onRemove }) => (
  <div data-testid="recent-users">
    <p className="text-xs font-medium text-muted-foreground mb-2">Usuarios recientes</p>
    <div className="grid grid-cols-2 gap-2">
      {users.map((u) => (
        <div key={u.email} className={`relative rounded-lg border transition-colors ${selected === u.email ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
          <button type="button" onClick={() => onPick(u)} data-testid={`recent-user-${u.email}`} className="w-full flex items-center gap-2 p-2 pr-6 text-left">
            <span className="w-8 h-8 shrink-0 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold font-head">{u.name?.[0]?.toUpperCase()}</span>
            <span className="min-w-0"><span className="block text-sm font-semibold truncate">{u.name}</span><span className="block text-[10px] text-primary">{ROLE_LABELS[u.role] || u.role}</span></span>
          </button>
          <button type="button" onClick={() => onRemove(u.email)} title="Quitar" data-testid={`recent-remove-${u.email}`} className="absolute top-1 right-1 text-muted-foreground hover:text-destructive"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  </div>
);

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState(getRecentUsers());
  const pwRef = useRef(null);

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const pick = (u) => { setEmail(u.email); setPassword(""); setTimeout(() => pwRef.current?.focus(), 0); };
  const remove = (em) => { removeRecentUser(em); setRecent(getRecentUsers()); if (email === em) setEmail(""); };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Bienvenido a NautaPOS");
      navigate("/");
    } catch (err) {
      toast.error(apiErr(err));
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full h-11 px-4 rounded-lg bg-secondary border border-border focus:border-primary focus:outline-none transition-colors";

  return (
    <div className="min-h-screen dark bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-primary/10 blur-[120px]" />
      <div className="absolute bottom-[-15%] left-[-10%] w-[400px] h-[400px] rounded-full bg-amber-500/10 blur-[120px]" />
      <div className="w-full max-w-md relative z-10 animate-fade-up">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center neon-glow mb-4">
            <Coffee className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Nauta<span className="text-primary neon-text">POS</span></h1>
          <p className="text-sm text-muted-foreground mt-1">Sistema de gestión · Sabornauta & Compunauta</p>
        </div>
        <form onSubmit={submit} autoComplete="off" className="card-surface p-8 space-y-5" data-testid="login-form">
          {recent.length > 0 && <RecentUsers users={recent} selected={email} onPick={pick} onRemove={remove} />}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Correo electrónico</label>
            <input type="email" name="nauta-user" autoComplete="off" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
              placeholder="usuario@nautapos.com" className={inputCls} />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Contraseña</label>
            <div className="relative">
              <input ref={pwRef} type={show ? "text" : "password"} name="nauta-pass" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
                placeholder="Ingresá tu contraseña" className={`${inputCls} pr-11`} />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground">
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full h-11 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors neon-glow flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Ingresar
          </button>
          <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="flex-1 h-px bg-border" />o<span className="flex-1 h-px bg-border" /></div>
          <button type="button" onClick={google} data-testid="login-google"
            className="w-full h-11 rounded-lg border border-border bg-white text-neutral-800 font-semibold hover:bg-neutral-100 transition-colors flex items-center justify-center gap-2">
            <GoogleIcon /> Ingresar con Google
          </button>
          <a href="/pedidos" className="block text-center text-xs text-muted-foreground hover:text-primary transition-colors" data-testid="public-link">
            ¿Eres cliente? Haz tu pedido online →
          </a>
        </form>
      </div>
    </div>
  );
}
